import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  extract_question_choice_items,
  normalize_choice_key,
  normalize_question_type,
} from '@/services/risk_management_service';
import { extract_error_message } from '@/utils/api_response_helpers';
import {
  GET_CHECKLIST_WEIGHTS,
  GET_ASSIGNED_QUESTIONS_TO_ASSESSMENT,
  SAVE_CHECKLIST_WEIGHTS,
  get_request_config,
} from '@/utils/system_constants/constant.js';

const first_defined_value = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }

  return undefined;
};

export default {
  name: 'ChecklistWeightings',
  mixins: [alert_mixin],
  props: {
    assessmentId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    checklist_questions: [],
    loading_questions: false,
    saving: false,
  }),
  computed: {
    total_questions() {
      return this.checklist_questions.length;
    },
    total_weight_inputs() {
      return this.checklist_questions.reduce((count, question) => count + question.options.length, 0);
    },
    has_questions() {
      return this.checklist_questions.length > 0;
    },
  },
  watch: {
    assessmentId: {
      immediate: true,
      handler() {
        this.load_questions();
      },
    },
  },
  methods: {
    get_route_assessment_id() {
      const route_query = this.$route?.query || {};
      const query_keys = ['risk_framework_assessment_id'];
      const key = query_keys.find((item) =>
        Reflect.has(route_query, item)
      );
      return Number(key ? Reflect.get(route_query, key) : null);
    },

    current_assessment_id() {
      const prop_value = Number(this.assessmentId);
      if (Number.isFinite(prop_value) && prop_value > 0) {
        return prop_value;
      }

      const route_value = this.get_route_assessment_id();
      return Number.isFinite(route_value) && route_value > 0
        ? route_value
        : null;
    },

    format_question_type_label(value) {
      return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
        .trim();
    },

    get_question_type(question) {
      const raw_type =
        question?.question_type?.type ||
        question?.question_type?.question_type ||
        question?.question_type ||
        question?.type ||
        '';

      return raw_type;
    },

    get_question_options(question) {
      const option_items = extract_question_choice_items(question).map((option) => {
        return {
          ...option,
          weight: 0,
        };
      });

      const options = option_items.length
        ? option_items
        : [
            { id: 1, label: 'Option 1', weight: 0 },
            { id: 2, label: 'Option 2', weight: 0 },
          ];

      return options;
    },

    get_question_id(question, index) {
      const question_id = Number(question?.question ?? question?.question_id ?? question?.id ?? index + 1);

      const final_id =
        Number.isFinite(question_id) && question_id > 0
          ? question_id
          : index + 1;

      return final_id;
    },

    is_supported_question_type(normalized_type) {
      const is_supported =
        normalized_type.includes('single_select') ||
        normalized_type.includes('multi_select');

      return is_supported;
    },

    get_saved_choice_label(choice) {
      const label = first_defined_value(choice?.option, choice?.label, choice?.name, '');

      return normalize_choice_key(label);
    },

    get_saved_choice_weight(choice) {
      return Number(choice?.weight) || 0;
    },

    add_saved_choice_to_map(accumulator, choice) {
      const option_label = this.get_saved_choice_label(choice);
      if (!option_label) {
        return accumulator;
      }

      accumulator.set(option_label, this.get_saved_choice_weight(choice));

      return accumulator;
    },

    build_question_row(question, index = 0) {
      const raw_type = this.get_question_type(question);
      const normalized_type = normalize_question_type(raw_type);
      const is_supported = this.is_supported_question_type(normalized_type);

      let formatted_question = null;

      if (is_supported) {
        formatted_question = {
          id: this.get_question_id(question, index),
          question: String(
            question?.text ||
            question?.question_text ||
            question?.question ||
            'Untitled Question'
          ),
          type: this.format_question_type_label(raw_type),
          options: this.get_question_options(question),
        };
      }

      return formatted_question;
    },
    apply_saved_weights(saved_rows = []) {
      const saved_map = new Map(
        (Array.isArray(saved_rows) ? saved_rows : [])
          .map((row) => {
            const question_id = Number(row?.question_id);
            if (!Number.isFinite(question_id) || question_id <= 0) return null;

            const choices = Array.isArray(row?.choices) ? row.choices : [];
            const normalized_choices = choices.reduce(
              (accumulator, choice) => this.add_saved_choice_to_map(accumulator, choice),
              new Map(),
            );

            return [question_id, normalized_choices];
          })
          .filter(Boolean)
      );

      this.checklist_questions = this.checklist_questions.map((question) => {
        const question_weights = saved_map.get(Number(question.id));
        if (!question_weights) {
          return question;
        }

        return {
          ...question,
          options: question.options.map((option) => ({
            ...option,
            weight: question_weights.get(normalize_choice_key(option.label)) ?? option.weight,
          })),
        };
      });
    },
    async load_saved_weights() {
      const assessment_id = this.current_assessment_id();
      if (!assessment_id || !this.checklist_questions.length) {
        return;
      }

      try {
        const { data } = await axios.get(
          GET_CHECKLIST_WEIGHTS,
          get_request_config({ params: { assessment_id } }),
        );

        if (data?.status === 'success') {
          this.apply_saved_weights(Array.isArray(data.data) ? data.data : []);
        }
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to load saved checklist weights.'));
      }
    },
    async load_questions() {
      this.loading_questions = true;

      try {
        const assessment_id = this.current_assessment_id();
        if (!assessment_id) {
          this.checklist_questions = [];
          return;
        }

        const { data } = await axios.get(
          GET_ASSIGNED_QUESTIONS_TO_ASSESSMENT,
          get_request_config({ params: { assessment_id } }),
        );

        const rows = data?.status === 'success' && Array.isArray(data.data)
          ? data.data
          : [];

        this.checklist_questions = rows
          .map((question, index) => this.build_question_row(question, index))
          .filter(Boolean);
        await this.load_saved_weights();
      } catch (error) {
        this.checklist_questions = [];
        this.show_error(extract_error_message(error, 'Failed to load assigned checklist questions.'));
      } finally {
        this.loading_questions = false;
      }
    },
    save_checklist_weightings() {
      const assessment_id = this.current_assessment_id();
      if (!assessment_id) {
        this.show_error('Missing risk framework assessment id.');
        return;
      }

      const items = this.checklist_questions.flatMap((question) => (
        question.options.map((option) => ({
          question_id: question.id,
          option_id: option.id,
          weight: Number(option.weight) || 0,
        }))
      ));

      this.saving = true;

      axios.post(
        SAVE_CHECKLIST_WEIGHTS,
        {
          assessment_id,
          items,
        },
        get_request_config(),
      )
        .then(({ data }) => {
          if (data?.status === 'success') {
            this.show_success(data?.message || 'Weights saved successfully');
            return;
          }

          this.show_error(data?.message || 'Failed to save checklist weights.');
        })
        .catch((error) => {
          this.show_error(error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Failed to save checklist weights.');
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
